import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, concat, of, from, forkJoin, Subject, Subscription } from 'rxjs';
import { map, finalize, mergeMap, toArray, takeUntil } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { saveAs } from 'file-saver';
import { environment } from 'src/environments/environment';
import { GetglobalsettingsService } from '../services/getglobalsettings.service';
import { Product, ProductInfo, ProdVendors } from '../shared/product';
import { ProductGroup } from '../shared/product-group';
import { ProductApiService } from '../services/product-api.service';
import { round2, stripVat } from '../shared/round';

import autoTable from 'jspdf-autotable';

/** Per-product data the detailed Excel exports need but the product list does not carry. */
interface ExportDetails {
  /** `GET /Product/:id/Info` row — stock, reserve, sold, on-way and the default vendor's price terms. */
  info: ProductInfo | null;
  /** Default vendor row (`ProdVendors.VENDORSITEMID == PRODPRICEVID`) from `GET /Product/:id/Vendors`. */
  vendor: ProdVendors | null;
}

/** Parallel requests while gathering export details — each product costs two API calls. */
const EXPORT_DETAILS_CONCURRENCY = 6;

@Component({
  selector: 'app-products2',
  templateUrl: './products2.component.html',
  styleUrls: ['./products2.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Advanced product manager — tree navigation, multi-select, group CRUD, and Excel export.
 *
 * Extends the simpler `ProductsComponent` with:
 * - Group management: create-on-level, rename (PUT via `putProdGroupById`), move, delete.
 * - Multi-select: `selectedProductList` collects checked rows; `allProductList` holds the
 *   full group content for export when no row is checked.
 * - Three Excel export variants: standard (`exportExcel`), with logistics costs
 *   (`onExportDataWithLogistic`), and with extended product info (`onExportDataWithExtendedInfo`).
 *   All write via `saveAsExcelFile` using `file-saver`.
 * - Product copy (`doCopyProduct`) via `postCopyProductInfo`, routing to the new ID.
 * - Product import dialog (`doProductImport`).
 *
 * `selectedGroupId` is persisted to `localStorage` under `'products-view-selected-group'`
 * and restored from route params on `ngOnInit`. `loadSelectedGroup` fetches the current
 * group name to update the browser title.
 */
export class Products2Component implements OnInit, OnDestroy {

  private _initialQuery: Subscription;

  private productQuery: Subscription;
  private prodGroupQuery: Subscription;
  private postGroupQuery: Subscription;
  private prodInfoQuery: Subscription;
  private product2Query: Subscription;
  private readonly destroy$ = new Subject<void>();
  /** Fires to abandon a detailed export still gathering data when another one starts. */
  private readonly _exportCancel$ = new Subject<void>();

  state: string = 'products-view';
  totalRecords: number;
  rows: number = 25;
  first: number;

  loading: boolean = false;

  searchtext: string;
  displayPS: boolean = false;

  selectedGroupId: number = 202; // 1241
  selectedProdId: number;

  reloadTree: any[] = null;

  usdCurrencyId: number;

  selectedGroup: ProductGroup;

  selectedProduct: Product = null;
  dummySelectedProduct: Product = null;
  selectedProductList: Product[] = [];

  allProductList: Product[] = [];

  productBookmark1: Product;
  productBookmark2: Product;
  productBookmark3: Product;

  /** Bookmark 1 button label — shows the pinned product code, or a default placeholder. */
  public get bookmarkText1(): string { return (this.productBookmark1) ? this.productBookmark1.PRODCODE : 'Закладка 1'; }
  /** Bookmark 2 button label — shows the pinned product code, or a default placeholder. */
  public get bookmarkText2(): string { return (this.productBookmark2) ? this.productBookmark2.PRODCODE : 'Закладка 2'; }
  /** Bookmark 3 button label — shows the pinned product code, or a default placeholder. */
  public get bookmarkText3(): string { return (this.productBookmark3) ? this.productBookmark3.PRODCODE : 'Закладка 3'; }

  /** `true` when no product is currently selected. */
  public get ifThereNoSelection(): boolean { return this.selectedProduct == null; }

  /** `true` when there is at least one product in the selection or group list for export. */
  public get canExport(): boolean { return (this.selectedProductList.length > 0) || (this.allProductList.length > 0); }

  /** `true` when bookmark slot 1 is occupied. */
  public get isThereBookmark1(): boolean { return (this.productBookmark1) ? true : false; }
  /** `true` when bookmark slot 2 is occupied. */
  public get isThereBookmark2(): boolean { return (this.productBookmark2) ? true : false; }
  /** `true` when bookmark slot 3 is occupied. */
  public get isThereBookmark3(): boolean { return (this.productBookmark3) ? true : false; }

  /** `true` when the selected product belongs to a different group than the one currently displayed. */
  public get onParentGroup(): boolean {
    return (this.selectedProduct && (this.selectedProduct.PRODGROUP != this.selectedGroupId));
  }

  items: MenuItem[] = [
    {
      label: 'Создать группу на этом уровне...', icon: 'pi pi-clone', command: () => { this.onCreateNewGroupOnThislevel(null); }
    },
      {
      label: 'Переименовать группу...', icon: 'pi pi-pencil', command: () => { this.onRenameGroup(null); }
      },
      {
        label: 'Перенести группу...', icon: 'pi pi-globe', command: () => { this.onMoveGroup(null); }
      },
      {
        label: 'Удалить группу...', icon: 'pi pi-trash', command: () => { this.onDeleteGroup(null); }
      },
  ];

  /*
   *
      PRODCODE: "ADAM-3011-AE"
      PRODVENDORNAME: "ADVANTECH"
      PRODCUSTCODE: "8537109890"
      PRODNAME: "Изолированный модуль Thermocouple Input Module"
      PRODDESCRIPTION: " Intelligent System Smart I/O & Communication"
      PRODGROUPNAME: "ADM3"
      PRODPRICEUAH: 6231.11532
      PRODPRICEUSD: 215.9832
      PRODBASEDISC: 5
      PRODQTYDISC: 10
      PRODDIALDISC: 24
      PRODSTOCK: 0
      PRODWEIGHT: 0.12
      PRODWWWPAGE: "https://www.proxis.ua/product/isolated-thermocouple-module-ADAM-3011/"
   */

  cols: any[] = [
    { field: 'PRODCODE',        header: 'Код товара' },
    { field: 'PRODVENDORNAME',  header: 'Поставщик' },
    { field: 'PRODCUSTCODE',    header: 'Код УКТЗЕД' },
    { field: 'PRODNAME',        header: 'Наименование' },
    { field: 'PRODDESCRIPTION', header: 'Описание' },
    { field: 'PRODGROUPNAME',   header: 'Группа' },
    { field: 'PRODPRICEUAH',    header: 'Розничная цена' },
    { field: 'PRODPRICEUSD',    header: 'Цена в валюте' },
    { field: 'PRODBASEDISC',    header: 'Минимальная скидка' },
    { field: 'PRODQTYDISC',     header: 'Скидка на количестве' },
    { field: 'PRODDIALDISC',    header: 'Партнерская скидка' },
    { field: 'PRODSTOCK',       header: 'Наличие' },
    { field: 'PRODWEIGHT',      header: 'Вес' },
    { field: 'PRODWWWPAGE',     header: 'Ссылка' },
  ];

  exportColumns: any[];

  excelExportItems: MenuItem[] = [
    {
      label: 'С логистикой', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.onExportDataWithLogistic();
      }
    },
    {
      label: 'Подробное', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.onExportDataWithExtendedInfo();
      }
    },
  ];

  contentReady: boolean = false;

  /** `true` while a detailed export is still gathering per-product data from the API. */
  exportingDetails: boolean = false;

  /** `true` when the export button should be disabled (content not ready, nothing to export, or an export is running). */
  public get disableExportButton(): boolean { return !this.contentReady || !this.canExport || this.exportingDetails; }

  displayDeleteGroup: boolean = false;
  displayMoveGroup: boolean = false;

  displayRenameGroup: boolean = false;
  groupName: string;

  displayCreateGroup: boolean = false;
  newGroupName: string;

  doProductImport: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private prodApi: ProductApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
    private messageService: MessageService
  ) { }

  /** Fetches the current group metadata and updates the browser title. */
  loadSelectedGroup() {
    this.prodGroupQuery = this.prodApi.getProdGroupById(this.selectedGroupId)
      .pipe(map(data => {
        this.selectedGroup = data;
        this.titleService.setTitle('Товары - ' + this.selectedGroup.PGROUPNAME);
      }))
      .subscribe();
  }

  /** Applies the group from the tree widget, clears the current selection, and calls `loadSelectedGroup`. */
  onSelectedGroup(event) {
    console.log('*** ProductsComponent selectedGroup: ' + JSON.stringify(event));

    this.selectedProduct = null;
    this.selectedProdId = 0;
    this.selectedGroupId = event.PGROUPID;

    this.loadSelectedGroup();

    localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));
}

  /** Persists `selectedGroupId` to localStorage and unsubscribes all queries. */
  ngOnDestroy(): void {
    console.log('*** ProductsComponent ngOnDestroy: ');

    localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));

    if (this.productQuery) { this.productQuery.unsubscribe(); }

    if (this.prodGroupQuery) { this.prodGroupQuery.unsubscribe(); }

    if (this.postGroupQuery) { this.postGroupQuery.unsubscribe(); }

    if (this.prodInfoQuery) { this.prodInfoQuery.unsubscribe(); }

    if (this.product2Query) { this.product2Query.unsubscribe(); }

    // Ends a detailed export still gathering data, so no file is saved after leaving the page.
    this.destroy$.next();
    this.destroy$.complete();

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
  }

  /** Restores last group from localStorage, reads route params, and calls `loadSelectedGroup`. */
  ngOnInit(): void {
    console.log('*** ProductsComponent ngOnInit: ');

    this.titleService.setTitle('Товары');

    this.exportColumns = this.cols.map(col => ({ title: col.header, dataKey: col.field }));
    environment.production || console.table(this.exportColumns);

    let storedGroup: string = localStorage.getItem(this.state + '-selected-group');

    this.usdCurrencyId = this.globalsettingsservice.getBalanceCurrencyId();

    if (storedGroup) {
      this.selectedGroupId = JSON.parse(storedGroup);
    }

    this._initialQuery =
      this.route.paramMap
        .subscribe((params: ParamMap) => {
          this.selectedGroupId = +params.get('id') || this.selectedGroupId;

          this.loadSelectedGroup();
        });
  }

  /** Navigates the tree to the selected product's own group and clears the selection. */
  goToProductGroup(event) {
    console.log('*** ProductsComponent goToProductGroup: ' + JSON.stringify(event));
    environment.production || console.table(this.selectedProduct);

    if (this.selectedProduct) {
      this.selectedGroupId = this.selectedProduct.PRODGROUP;
      this.selectedProduct = null;
      this.selectedProdId = 0;
      localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));
    }
  }

  /** Pins the selected product to bookmark 1, or navigates to the pinned product and clears the slot. */
  markBookmark1($event) {
    if (this.productBookmark1) {
      console.log('*** ProductsComponent markBookmark1: (moving) ' + this.productBookmark1.PRODID);

      if (this.selectedProduct.PRODID == this.productBookmark1.PRODID) this.productBookmark1 = null;
      else {
        this.selectedGroupId = this.productBookmark1.PRODGROUP;
        this.selectedProdId = this.productBookmark1.PRODID;
        this.selectedProduct = this.productBookmark1;

        localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));
      }
    } else if (this.selectedProduct) {
      this.productBookmark1 = this.selectedProduct;
      console.log('*** ProductsComponent markBookmark1: (marking) ' + this.productBookmark1.PRODID);
    }

  }

  /** Pins the selected product to bookmark 2, or navigates to the pinned product and clears the slot. */
  markBookmark2($event) {
    if (this.productBookmark2) {
      console.log('*** ProductsComponent markBookmark2: (moving) ' + this.productBookmark2.PRODID);

      if (this.selectedProduct.PRODID == this.productBookmark2.PRODID) this.productBookmark2 = null;
      else {
        this.selectedGroupId = this.productBookmark2.PRODGROUP;
        this.selectedProdId = this.productBookmark2.PRODID;
        this.selectedProduct = this.productBookmark2;

        localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));
      }
    } else if (this.selectedProduct) {
      this.productBookmark2 = this.selectedProduct;
      console.log('*** ProductsComponent markBookmark2: (marking) ' + this.productBookmark2.PRODID);
    }
  }

  /** Pins the selected product to bookmark 3, or navigates to the pinned product and clears the slot. */
  markBookmark3($event) {
    if (this.productBookmark3) {
      console.log('*** ProductsComponent markBookmark3: (moving) ' + this.productBookmark3.PRODID);

      if (this.selectedProduct.PRODID == this.productBookmark3.PRODID) this.productBookmark3 = null;
      else {
        this.selectedGroupId = this.productBookmark3.PRODGROUP;
        this.selectedProdId = this.productBookmark3.PRODID;
        this.selectedProduct = this.productBookmark3;

        localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));
      }
    } else if (this.selectedProduct) {
      this.productBookmark3 = this.selectedProduct;
      console.log('*** ProductsComponent markBookmark3: (marking) ' + this.productBookmark3.PRODID);
    }
  }

  /** Opens the product-search dialog. */
  searchProduct(event) {
    this.displayPS = true;
  }

  /** Logs keypress events from the search input (diagnostic only). */
  checkSearchKey(event) {
    console.log('*** ProductsComponent checkSearchKey: ' + JSON.stringify(event));
  }

  /** Applies multi-select changes from the child product-list: updates `selectedProductList` and current product. */
  onProductListSelection(event) {
    console.log('*** ProductsComponent onProductListSelection: ' + JSON.stringify(event));

    this.selectedProduct = null;
    this.selectedProdId = 0;
    this.selectedProductList = [];

    if (event) {
//      environment.production || console.table(event.current);
//      environment.production || console.table(event.list);

      this.selectedProductList = event.list;

      if (event.current && (this.selectedProduct != event.current)) {
        this.selectedGroupId = event.current.PRODGROUP;
        this.selectedProduct = event.current;
        localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));
      }
    }
  }

  /** Applies single-product selection, updating group and selection list. */
  onProductSelection(event) {
    console.log('*** ProductsComponent onProductSelection: ' + JSON.stringify(event));
    if (event) {

      if (this.selectedProduct != event) {
        this.selectedGroupId = event.PRODGROUP;
        this.selectedProdId = event.PRODID;
        this.selectedProductList = [this.selectedProduct];
      }
    } else {
      this.selectedProduct = null;
      this.selectedProdId = 0;
      this.selectedProductList = [];
    }
  }

  /** POSTs a server-side copy of the product and routes to the new product's editor. */
  doCopyProduct(event: Product) {
    console.log('*** ProductsComponent doCopyProduct: ' + JSON.stringify(event));
    this.product2Query =
      this.prodApi.postCopyProductInfo(event)
        .pipe()
        .subscribe((data) => {
          console.log('*** ProductsComponent doCopyProduct: *** on Post data map *** ' + JSON.stringify(data));
          let newProdId = data[''];  
          this.router.navigate(['/edit-product', newProdId]);
        });
  }

  /** Navigates to the product editor in new-product mode (id 0). */
  doNewProduct(event: Product) {
    console.log('*** ProductsComponent doNewProduct: ' + JSON.stringify(event));
    this.router.navigate(['/edit-product', 0]);
  }

  /** Delete stub — not yet implemented. */
  doDeleteProduct(event: Product) {
    console.log('*** ProductsComponent doDeleteProduct: ' + JSON.stringify(event));
  }

  /** Forces a tree/list reload by briefly resetting `selectedGroupId` to 0 then restoring it. */
  onReloadAllData(event) {
    console.log('*** ProductsComponent onReloadAllData: ' + JSON.stringify(event));

    /*
     * TODO: Need some trigger to invoke reload in child component
     */
    //let prod = this.selectedProdId;
    //this.selectedProdId = 0;
    //setTimeout(() => (this.selectedProdId = prod), 300);

    let group = this.selectedGroupId;
        this.selectedGroupId = 0;

    setTimeout(() => (this.selectedGroupId = group), 100);
    ;
  }

  /** PUTs the renamed group and triggers a reload. */
  onRenameGroupConfirm(event) {
    console.log('*** ProductsComponent onRenameGroupConfirm: ' + JSON.stringify(event));

    this.postGroupQuery =
      concat(
        this.prodApi.putProdGroupById({ ...this.selectedGroup, PGROUPNAME: event }))
      .subscribe(data => {
        console.log('*** ProductsComponent onRenameGroupConfirm: *** PUT *** ' + JSON.stringify(data));


        this.onReloadAllData(null);

        });
  }

  /** Opens the rename-group dialog pre-filled with the current group name. */
  onRenameGroup(event) {
    console.log('*** ProductsComponent onRenameGroup: ' + JSON.stringify(event));

    this.groupName = this.selectedGroup.PGROUPNAME;

    this.displayRenameGroup = true;
  }

  /** Confirm handler for the move-group dialog (stub). */
  onMoveGroupConfirm(event) {
    console.log('*** ProductsComponent onCreateGroupConfirm: ' + JSON.stringify(event));

  }

  /** Opens the move-group dialog. */
  onMoveGroup(event) {
    console.log('*** ProductsComponent onMoveGroup: ' + JSON.stringify(event));

    this.displayMoveGroup = true;
  }

  /** Confirm handler for the delete-group dialog (stub). */
  onDeleteGroupConfirm(event) {
    console.log('*** ProductsComponent onCreateGroupConfirm: ' + JSON.stringify(event));

  }

  /** Opens the delete-group confirmation dialog. */
  onDeleteGroup(event) {
    console.log('*** ProductsComponent onDeleteGroup: ' + JSON.stringify(event));

    this.displayDeleteGroup = true;
  }

  /** POSTs a new group at the current or parent level, then reloads the tree. */
  onCreateGroupConfirm(event: string) {
    console.log('*** ProductsComponent onCreateGroupConfirm: ' + JSON.stringify(event));

    // this.newGroupName = 'Новая группа товаров на этом уровне';
    this.reloadTree = null;

    this.postGroupQuery =
        this.prodApi.postProdGroupById({
          PGROUPID: -2,
          PGROUPCODE: event.slice(-15),     // get the end of name string to make it unique (16 is thge size of field)
          PGONLINE: false,
          PGROUPLEVEL: '',
          PGSTOCKCODE: 0,

          // Weird but very quick fix
          PGUPPERID: (this.newGroupName == 'Новая группа товаров на этом уровне') ? this.selectedGroup.PGUPPERID : this.selectedGroupId ,
          PGROUPNAME: event
        })
      .pipe(
        map(g => (this.selectedGroupId = g.PGROUPID)),

        finalize(() => {
          this.reloadTree = [];
          console.log('*** ProductsComponent onRenameGroupConfirm: *** TRIGGER *** ' + JSON.stringify(this.reloadTree))
      }))
      .subscribe(data => console.log('*** ProductsComponent onRenameGroupConfirm: *** POST *** ' + JSON.stringify(data)));
  }

  /** Opens the create-group dialog with a name preset for a same-level sibling group. */
  onCreateNewGroupOnThislevel(event) {
    console.log('*** ProductsComponent onCreateNewGroup: ' + JSON.stringify(event));

    this.newGroupName = 'Новая группа товаров на этом уровне';

    this.displayCreateGroup = true;
  }

  /** Opens the create-group dialog with a generic default group name. */
  onCreateNewGroup(event) {
    console.log('*** ProductsComponent onCreateNewGroup: ' + JSON.stringify(event));

    this.newGroupName = 'Новая группа товаров';

    this.displayCreateGroup = true;
  }

  /** Exports the product list to a PDF file using jspdf-autotable. */
  exportPdf() {
    import("jspdf").then(jsPDF => {
      import("jspdf-autotable").then(x => {
        const doc = new jsPDF.default('l', 'mm');

        console.log('*** ProductsComponent exportPdf: ' + JSON.stringify(doc));
        environment.production || console.table(this.exportColumns);

        autoTable(doc, { columns: this.exportColumns});
        doc.save('products.pdf');
      })
    })
  }

  /** Exports selected (or all) products to an Excel file with standard columns. */
  exportExcel() {
    import("xlsx").then(xlsx => {

      let exportData: any =
        ((this.selectedProductList.length > 0) ? this.selectedProductList : this.allProductList).map(e => ({

          'Код товара': e.PRODCODE,
          'Штрихкод (EAN)': e.PRODBARCODEMASK,
          'Наименование товара': e.PRODNAME,
          'Описание': e.PRODDESCRIPTION,
          'Склад': e.PRODSTOCK,
          'Цена с НДС': e.PRODPRICEUAH,
          'Цена в валюте': e.PRODPRICEUSD,

          // The discount tiers and the price in currency come with the list rows (GroupExFull);
          // _PRODUCTINFO is never loaded for them, so do not gate or read anything from it.
          'Минимальная скидка': e.PRODBASEDISC,
          'Средняя скидка': e.PRODQTYDISC,
          'Максимальная скидка': e.PRODDIALDISC,
          'Цена с мин скидкой': this.discountedPrice(e.PRODPRICEUSD, e.PRODBASEDISC, e.PRODVAT),
          'Цена со средней скидкой': this.discountedPrice(e.PRODPRICEUSD, e.PRODQTYDISC, e.PRODVAT),
          'Цена с макс скидкой': this.discountedPrice(e.PRODPRICEUSD, e.PRODDIALDISC, e.PRODVAT),

          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Вес': e.PRODWEIGHT,

          'Группа': e.PRODGROUPNAME,
          'Производитель': e.PRODVENDORNAME,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { products: worksheet }, SheetNames: ["products"] };

      //environment.production || console.table(this.selectedProductList);

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "products");
    });
  }

  /**
   * Price after a discount tier, in the currency of `price`, VAT-inclusive like `price` itself.
   *
   * The discount is applied as `price * (100 - discount) / 100` (the formula `WY_tvf_getProductInfo`
   * uses), then rounded the way the retail price is (edit-product "Желаемая розница"): strip the
   * product's own `PRODVAT`, round to a whole number, add the VAT back, i.e.
   * `round2(round(discounted / ((100 + vat) / 100)) * (100 + vat) / 100)`.
   *
   * `null` when the price, the discount or the VAT rate is missing, so an unknown figure is an empty
   * cell rather than a made-up number (a missing `PRODVAT` would otherwise turn into `NaN`).
   */
  private discountedPrice(
    price: number | null | undefined, discount: number | null | undefined, vat: number | null | undefined
  ): number | null {
    if (!Number.isFinite(price) || !Number.isFinite(discount) || !Number.isFinite(vat)) { return null; }

    const discounted = price * (100 - discount) / 100;

    return round2(Math.round(stripVat(discounted, vat)) * ((100.0 + vat) / 100.0));
  }

  /** Enables or disables export menu items based on `contentReady`. */
  checkCommand(event) {
    this.excelExportItems.forEach(e => e.disabled = !this.contentReady);
  }

  /** Products to export: the checked rows, or the whole group content when nothing is checked. */
  private get exportList(): Product[] {
    return (this.selectedProductList.length > 0) ? this.selectedProductList : this.allProductList;
  }

  /**
   * Gathers the per-product data the detailed exports need: `GET /Product/:id/Info` (stock, reserve,
   * sold, on-way, default vendor terms) and `GET /Product/:id/Vendors` (the default vendor row).
   *
   * The product list (`GET /Product/GroupExFull`) carries neither, and `product-list` has not loaded
   * them per row since the May 2026 stock refactor, so `_PRODUCTINFO`/`_PRODVENDORS` are never set on
   * the exported products. Fetched here, on demand, instead of per row on every list load. The
   * products themselves are not mutated. A failed request fails the whole export (the caller shows
   * the error) — an empty cell must never stand in for a missing answer.
   *
   * @param list Products about to be exported.
   * @returns Details keyed by `PRODID`.
   */
  private loadExportDetails(list: Product[]): Observable<Map<number, ExportDetails>> {
    const gs = this.globalsettingsservice.getGlobalSettings();
    const fd = new Date();

    return from(list).pipe(
      mergeMap(p => forkJoin({
        info: this.prodApi.getProdInfoById(
          p.PRODID, gs.mainCompanyId, this.usdCurrencyId,
          gs.mainStockId, gs.impCountFlag ? gs.impStockId : 0, gs.auxCountFlag ? gs.auxStockId : 0, fd),
        vendors: this.prodApi.getProdVendors(p.PRODID),
      }).pipe(
        map(r => [p.PRODID, {
          info: (r.info && r.info.length > 0) ? r.info[0] : null,
          vendor: (r.vendors || []).find(v => v.VENDORSITEMID == p.PRODPRICEVID) ?? null,
        } as ExportDetails] as [number, ExportDetails])
      ), EXPORT_DETAILS_CONCURRENCY),
      toArray(),
      map(pairs => new Map<number, ExportDetails>(pairs))
    );
  }

  /** Runs `build` once the export details are loaded; reports progress and failure to the user. */
  private exportWithDetails(build: (list: Product[], details: Map<number, ExportDetails>) => void): void {
    const list = this.exportList;

    // A newer export supersedes one still gathering data. Cancel first: the cancelled export's
    // finalize clears exportingDetails, which must not undo the flag set for this one.
    this._exportCancel$.next();

    this.exportingDetails = true;
    this.messageService.add({
      severity: 'info', summary: 'Export', life: 5000,
      detail: 'Loading details for ' + list.length + ' products, this can take a while'
    });

    this.loadExportDetails(list)
      .pipe(
        takeUntil(this._exportCancel$),
        takeUntil(this.destroy$),
        finalize(() => { this.exportingDetails = false; }))
      .subscribe({
        next: details => build(list, details),
        error: err => {
          console.error('*** ProductsComponent exportWithDetails error: ', err);
          this.messageService.add({
            severity: 'error', summary: 'Export failed',
            detail: err?.message || 'Could not load the product details needed for the export'
          });
        }
      });
  }

  /** Exports selected (or all) products with additional logistics columns (stock/reserve/vendor costs). */
  onExportDataWithLogistic() {
    this.exportWithDetails((list, details) => import("xlsx").then(xlsx => {

      let exportData: any = list.map(e => {
        const d = details.get(e.PRODID);
        const info = d?.info ?? null;
        const vendor = d?.vendor ?? null;

        return {

          'Код товара': e.PRODCODE,
          'Штрихкод (EAN)': e.PRODBARCODEMASK,
          'Наименование товара': e.PRODNAME,
          'Описание': e.PRODDESCRIPTION,
          'Склад': e.PRODSTOCK,
          'Цена с НДС': e.PRODPRICEUAH,
          'Цена в валюте': e.PRODPRICEUSD,

          'Минимальная скидка': info ? e.PRODBASEDISC : null,
          'Средняя скидка': info ? e.PRODQTYDISC : null,
          'Максимальная скидка': info ? e.PRODDIALDISC : null,
          'Цена с мин скидкой': info?.BASEDISCPRICE ?? null,
          'Цена со средней скидкой': info?.QTYDISCPRICE ?? null,
          'Цена с макс скидкой': info?.DIALDISCPRICE ?? null,

          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Вес': e.PRODWEIGHT,
          'Срок поставки': e.PRODDELTERM,

          'Группа': e.PRODGROUPNAME,
          'Производитель': e.PRODVENDORNAME,

          'На складе': info?.PRODSTOCK ?? null,
          'В резерве': info?.PRODRESERVE ?? null,
          'Продано': info?.PRODSOLD ?? null,
          'В пути': info?.PRODONWAY ?? null,

          'Поставщик': info?.VENDORNAME ?? vendor?.VENDORNIKNAME ?? null,
          'Цена поставщика': info?.VENDORPRICE ?? vendor?.VENDORPRICE ?? null,
          'Доставка': info?.VENADDCONST ?? vendor?.VPADDCONST ?? null,
          'Пошлина': info?.VENMULCONST ?? vendor?.VPMULCONST ?? null,
          'Дополнительно +': info?.VENADDVAR ?? vendor?.VPADDVAR ?? null,
          'Дополнительно *': info?.VENMULVAR ?? vendor?.VPMULVAR ?? null,
          'Маржа': vendor?.VENDORMARGIN ?? null,
          'Округление': vendor?.VENROUNDBASE ?? null,
          'Старый расчет входной цены': vendor?.VPINCOMPRICEXPR ?? null,
          'Старый расчет выходной цены': vendor?.EUPRICEEXPR ?? null,
          'Примечание': vendor?.VENDORNOTE ?? null,
        };
      });

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { products: worksheet }, SheetNames: ["products"] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "products");
    }));
  }

  /** Exports selected (or all) products with all extended info columns including packaging and vendor price formulas. */
  onExportDataWithExtendedInfo() {
    this.exportWithDetails((list, details) => import("xlsx").then(xlsx => {

      let exportData: any = list.map(e => {
        const d = details.get(e.PRODID);
        const info = d?.info ?? null;
        const vendor = d?.vendor ?? null;

        return {

          'Код товара': e.PRODCODE,
          'Артикул': e.PRODARTICUL,
          'Штрихкод (EAN)': e.PRODBARCODEMASK,
          'Наименование товара': e.PRODNAME,
          'Описание': e.PRODDESCRIPTION,
          'Для бухгалтерии': e.PRODNOTE,
          'Склад': e.PRODSTOCK ?? 0,
          'Цена с НДС': e.PRODPRICEUAH,
          'Цена в валюте': e.PRODPRICEUSD,

          'Минимальная скидка': info ? e.PRODBASEDISC : null,
          'Средняя скидка': info ? e.PRODQTYDISC : null,
          'Максимальная скидка': info ? e.PRODDIALDISC : null,
          'Цена с мин скидкой': info?.BASEDISCPRICE ?? null,
          'Цена со средней скидкой': info?.QTYDISCPRICE ?? null,
          'Цена с макс скидкой': info?.DIALDISCPRICE ?? null,

          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Вес': e.PRODWEIGHT,

          'В упаковке': e.PRODPACK,
          'Вес упаковки': e.PRODPACKGW,
          'Объем упаковки': e.PRODPACKVOL,
          'Гарантия': e.PRODWARRANTY,
          'Срок поставки': e.PRODDELTERM,

          'Группа': e.PRODGROUPNAME,
          'Производитель': e.PRODVENDORNAME,

          'На складе': info?.PRODSTOCK ?? null,
          'В резерве': info?.PRODRESERVE ?? null,
          'Продано': info?.PRODSOLD ?? null,
          'В пути': info?.PRODONWAY ?? null,

          'Поставщик': info?.VENDORNAME ?? vendor?.VENDORNIKNAME ?? null,
          'Цена поставщика': info?.VENDORPRICE ?? vendor?.VENDORPRICE ?? null,
          'Валюта поставщика': vendor?.CURCODE ?? info?.VENDORCURCODE ?? null,

          'Доставка': info?.VENADDCONST ?? vendor?.VPADDCONST ?? null,
          'Пошлина': info?.VENMULCONST ?? vendor?.VPMULCONST ?? null,
          'Дополнительно +': info?.VENADDVAR ?? vendor?.VPADDVAR ?? null,
          'Дополнительно *': info?.VENMULVAR ?? vendor?.VPMULVAR ?? null,
          'Маржа': vendor?.VENDORMARGIN ?? null,
          'Округление': vendor?.VENROUNDBASE ?? null,
          'Старый расчет входной цены': vendor?.VPINCOMPRICEXPR ?? null,
          'Старый расчет выходной цены': vendor?.EUPRICEEXPR ?? null,
          'Примечание': vendor?.VENDORNOTE ?? null,

        };
      });

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { products: worksheet }, SheetNames: ["products"] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "products");
    }));
  }

  /** Triggers a browser download of the given XLSX buffer using `file-saver`. */
  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /** Updates `usdCurrencyId` when the currency selector changes. */
  onCurrencyChange(event) {
    console.log('*** ProductListComponent onCurrencyChange: ' + event);
    this.usdCurrencyId = event;
    // this.listOptions$.next({ obsolete: this.showObsoleteProducts, balCurr: this.usdCurrencyId, group: this.groupId, filter: this.filters });
  }

  /** Called when the import dialog signals completion — triggers a full data reload. */
  importIsDone(event) {
    console.log('*** ProductListComponent importIsDone: ' + event);
    this.onReloadAllData(null);
  }
}

